import {Checkout} from "@connox/typescript-library";
import {ICartData} from "@connox/typescript-library/types/cart/CartData";
import {BraintreeLineItem} from "@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes";
import {displayName, isoLocales} from "../../PaymentController";
import {
  BraintreePayPalCheckoutCreatePaymentOptions,
  BraintreeShippingOption,
} from "@paypal/react-paypal-js/dist/types/types/braintree/paypalCheckout";
import {IGetCheckoutShippingOptionsData} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import {getShopId} from "../../../getShopId";
import {convertAddress} from "./paypalAddressHelpers";
import {PaymentOptionInputParamType} from "./paypalTypes";

type LineItemKind = "debit" | "credit";

// region assembling paypal payment options

/**
 * Convert our shippings to braintree ones
 * @param apiShippingOptions all options as array
 * @param checkout needed for `logisticsTypeId` and `isNet` from cartData
 */
const gatherShippingOptions = (
  apiShippingOptions: IGetCheckoutShippingOptionsData[] | undefined,
  checkout: Checkout,
): BraintreeShippingOption[] | undefined => {
  let shippingOptions: BraintreeShippingOption[] | undefined;
  if (apiShippingOptions) {
    const selectedShippingOption =
      apiShippingOptions.find((shipping) => shipping.logisticsTypeId === checkout.logisticsTypeId) ||
      apiShippingOptions.find((shipping) => !!shipping.isCustomerPreferredShipping) ||
      apiShippingOptions.find((shipping) => shipping.isBestMatch) ||
      apiShippingOptions[0];

    shippingOptions = apiShippingOptions.map((shipping) => {
      let value = shipping.amount.amount;
      if (checkout.cartData.isNet) {
        value = `${Math.round(Number(shipping.amount.amount) / 0.0119) / 100}`;
      }

      return {
        id: String(shipping.logisticsTypeId),
        label: shipping.name,
        type: "SHIPPING" as "SHIPPING" | "PICKUP",
        selected: shipping.logisticsTypeId === selectedShippingOption.logisticsTypeId,
        amount: {
          value,
          currency: shipping.amount.currencyCode,
        },
      };
    });
  }

  return shippingOptions;
};

/**
 * Give amount of cart
 * @param cartData cart data containing all positions
 * @param isTotal if true: give total, if false: give total minus the shipping costs
 */
const getCartAmount = (cartData: ICartData, isTotal: boolean): number => {
  let totalCartAmount = Number(cartData.value.payable.amount);
  if (!isTotal) {
    const shippingPos = cartData.positions.find((pos) => pos.type === "Shipping");
    const shippingAmount = Number(shippingPos?.amount.amount ?? "0");
    totalCartAmount = totalCartAmount - shippingAmount;
  }

  return totalCartAmount;
};

/**
 * Build up all payment options
 * @param params `PaymentOptionInputParamType`
 */
export const getPaymentOptions = (params: PaymentOptionInputParamType): BraintreePayPalCheckoutCreatePaymentOptions => {
  const {apiShippingOptions, checkout, deliveryAddress} = params;

  // gather shipping option data, if available
  const shippingOptions = gatherShippingOptions(apiShippingOptions, checkout);
  // convert address to override
  const shippingAddressOverride = convertAddress(deliveryAddress);
  // get total amount, either with or without shipping costs
  const amount = getCartAmount(checkout.cartData, !apiShippingOptions);
  // get line items, either with or without shipping costs
  const lineItems = getLineItems(checkout.cartData, !apiShippingOptions);

  return {
    flow: "checkout",
    amount,
    currency: checkout.cartData.currencyCode,
    displayName,
    intent: "authorize",
    shippingOptions,
    enableShippingAddress: true,
    shippingAddressEditable: !!apiShippingOptions,
    shippingAddressOverride,
    lineItems,
  };
};

/**
 * Builds up the line items for the order of paypal
 * @param cartData cart data containing the positions
 * @param isShippingIncluded total, including shipping, or without shipping
 */
function getLineItems(cartData: ICartData, isShippingIncluded: boolean): BraintreeLineItem[] {
  let cartDataPositions = cartData.positions;
  if (!isShippingIncluded) {
    cartDataPositions = cartData.positions.filter((pos) => pos.type !== "Shipping");
  }
  const itemPositions = cartDataPositions.map<BraintreeLineItem>((position) => {
    const {item, price, quantity, text} = position;
    const numberPriceAmount = Number(price.amount);
    const tempObject = {
      kind: (quantity < 0 !== numberPriceAmount < 0 ? "credit" : "debit") as LineItemKind,
      name: text,
      quantity: Math.abs(quantity).toString(),
      unitAmount: Math.abs(numberPriceAmount).toString(),
      unitTaxAmount: undefined,
      description: item?.title,
      productCode: undefined,
      url: undefined,
    };

    if (position.type === "Item" && item) {
      return {...tempObject, productCode: item.itemId.toString(), url: item.url};
    }

    return tempObject;
  });

  const rebatePositions = cartData.rebates.map<BraintreeLineItem>((rebate) => {
    return {
      kind: "credit" as LineItemKind,
      name: rebate.code,
      quantity: "1",
      unitAmount: rebate.amount.amount,
      description: undefined,
      unitTaxAmount: undefined,
      productCode: undefined,
      url: undefined,
    };
  });

  return [...itemPositions, ...rebatePositions];
}

// endregion assembling paypal payment options

export const isPayLaterAllowed = (): boolean => {
  // Restriction via sales channel is also done by xsl
  // so there is no unnecessary blank space on the page

  return (
    ["EUR", "GBP", "USD"].includes(window.c_ActiveCurrency) &&
    ["de_DE", "fr_FR", "en_US", "en_GB"].includes(isoLocales[getShopId()])
  );
};

/**
 * Locale setting is just for testing environment.
 * Should not be set in live environment
 */
export function getPayPalLocale(): string | undefined {
  let locale: string | undefined;
  if (__DEVELOPMENT__) {
    locale = isoLocales[getShopId()];
  }

  return locale;
}

/**
 * Only used in the `PayPalPayLaterMessage.tsx`
 * See <https://connox.myjetbrains.com/youtrack/issue/FE-101127/PP-Pay-Later-Message-buyerCountry-wieder-einbauen>
 */
export function getBuyerCountryForMessage(): string | undefined {
  let buyerCountry: string | undefined;
  const current = isoLocales[getShopId()];

  if (__DEVELOPMENT__) {
    return current.split("_")[1];
  }

  const useBuyerCountry = document.body.querySelector<HTMLSpanElement>("span.price-value[data-use-buyer-country]")
    ?.dataset.useBuyerCountry;
  if (useBuyerCountry?.includes(current)) {
    buyerCountry = current.split("_")[1];
  }

  return buyerCountry;
}
